import React, { useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { theme } from '@kardume/theme';
import { NotificationProvider } from '@kardume/hooks';
import { authService, adminApi } from '@kardume/services';
import type { MerchantUserDto } from '@kardume/types';
import { LoginScreen } from '@kardume/ui';
import MainLayout from './layouts/MainLayout';
import ChangePasswordModal from './components/ChangePasswordModal';
import { AdminRoutes, PublicRoute, resolvePublicRoute } from './routes';
import { AdminSectionId, getInitialSection, getVisibleNavItems, isAdminSection } from './utils/navigation';

const SECTION_KEY = '@KardumeUrbano:admin:activeSection';

function App() {
  /** Public routes (e.g. the onboarding wizard) bypass the auth gate entirely. */
  const publicRoute = useMemo(() => resolvePublicRoute(window.location.pathname), []);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<MerchantUserDto | null>(null);
  const [pendingChange, setPendingChange] = useState<{ email: string; currentPassword: string } | null>(null);
  const [activeSection, setActiveSection] = useState<AdminSectionId>(() => {
    const stored = localStorage.getItem(SECTION_KEY);
    return isAdminSection(stored) ? stored : 'dashboard';
  });

  useEffect(() => {
    localStorage.setItem(SECTION_KEY, activeSection);
  }, [activeSection]);

  const isOwner = user?.role === 'OWNER';
  const permissions = useMemo(() => user?.effectivePermissions ?? [], [user]);
  const navigationItems = useMemo(() => getVisibleNavItems(permissions, isOwner), [permissions, isOwner]);

  const loadSession = async () => {
    try {
      const { user: me } = await adminApi.getMe();
      setUser(me);
      const next = getInitialSection(me.effectivePermissions, activeSection, me.role === 'OWNER');
      if (next) setActiveSection(next);
    } catch {
      authService.logout('admin');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (publicRoute) {
      setLoading(false);
    } else if (authService.isAuthenticated()) {
      loadSession();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (email: string, password: string) => {
    const result = await authService.login({ email, password });
    if (result.authStatus === 'PASSWORD_CHANGE_REQUIRED') {
      setPendingChange({ email, currentPassword: password });
      return;
    }
    await loadSession();
  };

  const handleLogout = () => {
    authService.logout('admin');
    setUser(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <NotificationProvider>
        <Toaster position="top-right" />
        {publicRoute ? (
          <PublicRoute route={publicRoute} />
        ) : loading ? (
          <div style={{ minHeight: '100vh' }} />
        ) : !user ? (
          <>
            <LoginScreen
              withBackground
              backgroundUrl="https://images.unsplash.com/photo-1758272133693-d2124dbe00de?auto=format&fit=crop&w=1600&q=80"
              panelTitle="A plataforma que fortalece o comércio local."
              panelSubtitle="Acompanhe suas métricas, publique anúncios e fidelize seus clientes no Kardume Urbano."
              formTitle="Administração"
              signUpHref="/cadastro"
              onSubmit={handleLogin}
              onForgotPassword={authService.forgotPassword}
            />
            {pendingChange && (
              <ChangePasswordModal
                email={pendingChange.email}
                currentPassword={pendingChange.currentPassword}
                onClose={() => setPendingChange(null)}
                onSuccess={async () => {
                  setPendingChange(null);
                  await loadSession();
                }}
              />
            )}
          </>
        ) : (
          <MainLayout
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            onLogout={handleLogout}
            merchantName={user.merchant?.tradeName || user.merchant?.name}
            userName={`${user.firstName} ${user.lastName}`}
            navItems={navigationItems}
          >
            <AdminRoutes
              activeSection={activeSection}
              user={user}
              onUserUpdated={setUser}
              permissions={permissions}
              isOwner={isOwner}
            />
          </MainLayout>
        )}
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
